import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import isArray from "lodash/isArray";
import isNil from "lodash/isNil";
import { useDispatch, useSelector } from "react-redux";

import Flx from "../../components/layout/Flx";
import Screen from "../../components/layout/Screen";
import TitledHeader from "../../components/layout/TitledHeader";

import {
  AssignmentOutlined,
  Fullscreen,
  FullscreenExit,
  KeyboardArrowDown,
  SortRounded,
} from "@mui/icons-material";
import { Box } from "@mui/material";
import { blue, blueGrey, grey } from "@mui/material/colors";
import { isEmpty, size } from "lodash";
import useLocalStorageState from "use-local-storage-state";
import AgGridAppliedFilters from "../../components/agGrid/AgGridAppliedFilters";
import UpdateLoanDataModal from "../../components/common/Loan/UpdateLoanDataModal";
import SearchInput from "../../components/inputs/SearchInput";
import LoadingFallback from "../../components/LoadingFallback";
import ToggleNavigation from "../../components/navigation/ToggleNavigation";
import Txt from "../../components/typography/Txt";
import TableSortDropdown from "../../components/ui/TableSortDropdown";
import { LOCAL_STORAGE_KEYS } from "../../constants/keyNames/localStorage";
import { LOAN_PIPELINE_FILTER_LABEL_ALL } from "../../constants/loanPipeline/LOAN_PIPELINE_FILTER_LABEL_ALL";
import { LOAN_STATUS_LABEL_ALL_ACTIVE } from "../../constants/loanPipeline/LOAN_STATUS_LABEL_ALL_ACTIVE";
import { selectOptionsLoanStatus } from "../../constants/selectOptions/selectOptionsLoanStatus";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import { loanDrilldownClear } from "../../redux/actions/loanDrilldownActions";
import { loanPipelineSet } from "../../redux/actions/loanPipelineActions";
import CreateLoanButton from "./buttons/CreateLoanButton";
import DownloadPipelineAsZipButton from "./buttons/DownloadPipelineAsZipButton";
import ScreenBodyWrapper from "./components/ScreenBodyWrapper";
import LoanPipelineSidebar from "./LoanPipelineSidebar/LoanPipelineSidebar";
import LoanPipelineTable from "./LoanPipelineTable/LoanPipelineTable";
import LoanPipelineSelector from "./selectors/LoanPipelineSelector";
import { useReduxHook } from "../../hooks/useReduxHook";
import { sidebarToggle } from "../../redux/actions/sidebarActions";
import { navSidebarToggle } from "../../redux/actions/navSidebarActions";

const LoanPipelineScreen = () => {
  const ref = useRef();
  const [quickFilter, setQuickFilter] = useState(null);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanPipelinePreFiltered = useSelector((state) => state?.loanPipeline);
  const { isDevUser } = useReduxHook();
  const loanPipeline = useMemo(() => {
    if (!isDevUser) {
      const filtered = loanPipelinePreFiltered?.filter(
        (l) =>
          l?.pipeline !== "joliva@cliffcomortgage.com" &&
          l?.salesperson !== "joliva@cliffcomortgage.com"
      );
      return filtered;
    }
    return loanPipelinePreFiltered;
  }, [loanPipelinePreFiltered, isDevUser]);

  const { loading, getUserPipeline } = usePipelineHook();
  const dispatch = useDispatch();

  const isFetchingRef = useRef(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [pipeline, setPipeline] = useLocalStorageState(
    LOCAL_STORAGE_KEYS.LOAN_PIPELINE_TABLE.DEFAULT_TABLE_PIPELINE_FILTER,
    { defaultValue: "Underwriting" }
  );
  const [loanStatusFilter, setLoanStatusFilter] = useLocalStorageState(
    LOCAL_STORAGE_KEYS.LOAN_PIPELINE_TABLE.DEFAULT_TABLE_STATUS_FILTER,
    { defaultValue: LOAN_STATUS_LABEL_ALL_ACTIVE }
  );

  const [sortFilter, setSortFilter] = useLocalStorageState(
    LOCAL_STORAGE_KEYS.LOAN_PIPELINE_TABLE.DEFAULT_TABLE_SORT,
    {
      defaultValue: {
        colId: "updateDateTime",
        sort: "desc",
      },
    }
  );

  const [salespersonFilter, setSalespersonFilter] = useLocalStorageState(
    LOCAL_STORAGE_KEYS.LOAN_PIPELINE_TABLE.DEFAULT_TABLE_SALESPERSON_FILTER,
    {
      defaultValue: {
        colId: "salesperson",
        sort: "desc",
      },
    }
  );

  const [pipelineSelectorCollapsed, setPipelineSelectorCollapsed] =
    useLocalStorageState(
      LOCAL_STORAGE_KEYS.LOAN_PIPELINE_TABLE
        .DEFAULT_TABLE_PIPELINE_FILTER_COLLAPSED,
      {
        defaultValue: false,
      }
    );

  const [advancedFiltering, setAdvancedFiltering] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);

  const onPipelineStatusChange = (newPipeline) => {
    setPipeline(newPipeline);
  };

  const fetchLoanPipeline = useCallback(() => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;

    getUserPipeline({
      onSuccessFn: (data) => {
        dispatch(loanPipelineSet(data));
        isFetchingRef.current = false;
      },
      onErrorFn: (err) => {
        console.error("GetUserPipeline error:", err);
        isFetchingRef.current = false;
      },
    });
  }, [getUserPipeline, dispatch]);

  useEffect(() => {
    if (isNil(loanPipeline) && !isFetchingRef.current) {
      fetchLoanPipeline();
    }
    if (!isNil(loanDrilldown)) {
      dispatch(loanDrilldownClear());
    }
  }, [loanPipeline, fetchLoanPipeline, loanDrilldown, dispatch]);

  const onLoanSelect = useCallback((data, field) => {
    setSelectedLoan({ loan: data, field });
  }, []);

  const onLoanSelectCancel = useCallback(() => {
    setSelectedLoan(null);
  }, []);

  const onSortFilterChange = useCallback(
    (sortState, suppressToggle) => {
      // console.log("1. sortState", sortState);
      const { colId, sort } = sortState;

      const sortValue =
        sortFilter?.colId === colId && sortFilter?.sort === sort ? null : sort;
      let finalSortState = { colId, sort: sortValue };
      if (suppressToggle) {
        finalSortState = sortState;
      }
      // console.log("2. finalSortState", finalSortState);
      if (ref.current && ref.current.api && ref.current.api.applyColumnState) {
        const columnState = [finalSortState];
        ref.current.api.applyColumnState({
          state: columnState,
          defaultState: { sort: null },
        });

        setSortFilter(finalSortState);
      }
    },
    [sortFilter]
  );

  const onFirstDataRendered = useCallback(() => {
    onSortFilterChange(sortFilter, true);
  }, [onSortFilterChange, sortFilter]);

  const onFullScreenToggle = useCallback(
    (props) => {
      setFullScreen((prev) => !prev);
      dispatch(navSidebarToggle());
    },
    [dispatch]
  );
  return (
    <Screen>
      {fullScreen ? null : (
        <TitledHeader
          hideBottomBorder
          title={"Loan Pipeline"}
          titleEndContent={
            <Tooltip title="Refresh loan data">
              <IconButton
                size="small"
                onClick={() => dispatch(loanPipelineSet(null))}
              >
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          }
          quickFilter={quickFilter}
          sx={{ pb: 1 }}
          setQuickFilter={setQuickFilter}
          endContent={
            <Flx gap={1} jb ac>
              <CreateLoanButton />
              <DownloadPipelineAsZipButton />
            </Flx>
          }
        >
          <Box
            sx={{
              border: `1px solid ${grey[200]}`,
              backgroundColor: grey[50],
              borderRadius: 1,
              p: 0.4,
              display: "flex",
              flexDirection: "column",
              width: "max-content",
              gap: 0.2,
              ".MuiButtonBase-root": {
                p: "4px 6px",
                fontSize: "12px",
              },
            }}
          >
            <Flx ac jb>
              <LoanPipelineSelector
                value={pipeline}
                onChange={onPipelineStatusChange}
              />
              <Flx center>
                <Tooltip title={"Toggle Loan Status"}>
                  <IconButton
                    size="small"
                    onClick={() => setPipelineSelectorCollapsed((s) => !s)}
                  >
                    <KeyboardArrowDown
                      // fontSize="small"
                      sx={{
                        transform: pipelineSelectorCollapsed
                          ? "rotate(0deg)"
                          : "rotate(180deg)",
                        transition: "transform 0.2s",
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Flx>
            </Flx>
            {!pipelineSelectorCollapsed && (
              <LoanPipelineStatusSortDropdown
                pipelineFilter={pipeline}
                selectedSort={loanStatusFilter}
                navigation
                onSortChange={setLoanStatusFilter}
              />
            )}
          </Box>
        </TitledHeader>
      )}

      <Flx
        fw
        jb
        ac
        sx={{
          // pr: 0.8,
          p: 1,
          borderTop: `1px solid ${grey[300]}`,

          ".pipeline-selector .MuiButtonBase-root": {
            px: 1.6,
            fontSize: "13px",
            // background: "red",
            m: 0,
          },
          ".status-selector": {
            mt: 0.2,
            mb: 0.1,
            ml: 1,
            background: "#2962ff14",
            border: `1px solid ${blueGrey[50]}`,
            ".MuiButtonBase-root": {
              px: 1.3,
              py: 0.6,
              fontSize: "10px",
              // background: "red",
              m: 0,
            },
          },
        }}
      >
        <Flx fw jb ac wrap gap={2} sx={{ px: 1 }}>
          <Flx gap={1.5} ac>
            <SearchInput
              value={quickFilter}
              onChange={setQuickFilter}
              // fullWidth
              placeholder={"Search in table..."}
              sx={{
                width: "225px",
                ".MuiInputBase-root": {
                  borderRadius: "50px !important",
                  background: "#ffffff",
                  fontSize: "11px",
                  pl: 1.2,
                  pr: 0.5,
                  ".MuiSvgIcon-root": {
                    fontSize: "11px",
                  },
                },
                input: {
                  py: 1,
                  height: "unset",
                  borderRadius: "50px",
                  fontSize: "11px",
                },
              }}
            />
            <Flx ac gap={0.6} sx={{ ml: 1 }}>
              <Flx ac gap={0.4}>
                <AssignmentOutlined
                  // className="thin"
                  sx={{ fontSize: "15px", opacity: 0.5 }}
                />
                <Txt sx={{ fontSize: "11px" }} secondary>
                  Loan Status
                </Txt>
              </Flx>

              <LoanPipelineStatusSortDropdown
                pipelineFilter={pipeline}
                selectedSort={loanStatusFilter}
                onSortChange={setLoanStatusFilter}
              />
            </Flx>
            <Flx ac gap={0.6} sx={{ ml: 1 }}>
              <Flx ac gap={0.4}>
                <SortRounded sx={{ fontSize: "15px", opacity: 0.5 }} />
                <Txt sx={{ fontSize: "11px" }} secondary>
                  Sort By
                </Txt>
              </Flx>

              <LoanPipelineSortDropdown
                selectedSort={sortFilter}
                onSortChange={onSortFilterChange}
              />
            </Flx>
          </Flx>
          <Flx ac gap={1}>
            <AgGridAppliedFilters
              ref={ref}
              advancedFiltering={advancedFiltering}
              onAdvancedFilteringClick={setAdvancedFiltering}
              onFilterRemove={(columnId, gridRef) => {
                console.log("Filter removed:", columnId);
              }}
              onAllFiltersRemove={(gridRef) => {
                console.log("All filters cleared");
              }}
            />
            <Tooltip
              title={fullScreen ? "Exit Full Screen" : "Enter Full Screen"}
            >
              <IconButton
                sx={{ background: fullScreen ? blue[50] : "transparent" }}
                onClick={onFullScreenToggle}
              >
                {fullScreen ? (
                  <FullscreenExit
                    className="thin-8"
                    sx={{ color: "#2962ff" }}
                  />
                ) : (
                  <Fullscreen className="thin-8" />
                )}
              </IconButton>
            </Tooltip>
          </Flx>
        </Flx>
      </Flx>

      <Suspense fallback={<LoadingFallback />}>
        <ScreenBodyWrapper>
          <LoanPipelineTable
            ref={ref}
            rowData={loanPipeline}
            loading={loading}
            quickFilterText={quickFilter}
            onLoanSelect={onLoanSelect}
            pipelineFilter={pipeline}
            advancedFiltering={advancedFiltering}
            loanStatusFilter={loanStatusFilter}
            onFirstDataRendered={onFirstDataRendered}
          />

          <UpdateLoanDataModal
            loan={selectedLoan?.loan}
            field={selectedLoan?.field}
            onClose={onLoanSelectCancel}
          />
        </ScreenBodyWrapper>
      </Suspense>
    </Screen>
  );
};

const LoanPipelineStatusSortDropdown = ({
  pipelineFilter,
  selectedSort,
  onSortChange,
  navigation,
  sx = {},
}) => {
  const loanPipeline = useSelector((state) => state.loanPipeline);

  const filteredPipelineLoans = useMemo(() => {
    if (pipelineFilter === LOAN_PIPELINE_FILTER_LABEL_ALL) {
      return loanPipeline;
    }
    if (isArray(loanPipeline)) {
      return loanPipeline.filter((loan) => loan?.pipeline === pipelineFilter);
    }
    return [];
  }, [loanPipeline, pipelineFilter]);

  // --

  const getPipelineLabel = (pipeline, status) => {
    if (status === "All") {
      return `All (${size(pipeline)})`;
    }
    if (status === LOAN_STATUS_LABEL_ALL_ACTIVE) {
      const activeStatusList = [
        "Prospect",
        "Terms Accepted",
        "Processing",
        "Appraisal Ordered",
        "Appraisal In",
        "Underwriting",
        "Final Review",
        "Clear To Close",
      ];
      const count = size(
        pipeline?.filter((loan) => activeStatusList.includes(loan?.loanStatus))
      );
      return `${status} (${count})`;
    }
    const count = size(pipeline?.filter((loan) => loan?.loanStatus === status));
    return `${status} (${count})`;
  };

  // Helper function to get the count for a status
  const getStatusWithCount = (pipeline, status) => {
    if (status === "All") {
      return size(pipeline);
    }
    if (status === LOAN_STATUS_LABEL_ALL_ACTIVE) {
      const activeStatusList = [
        "Prospect",
        "Terms Accepted",
        "Processing",
        "Appraisal Ordered",
        "Appraisal In",
        "Underwriting",
        "Final Review",
        "Clear To Close",
      ];
      return size(
        pipeline?.filter((loan) => activeStatusList.includes(loan?.loanStatus))
      );
    }
    return size(pipeline?.filter((loan) => loan?.loanStatus === status));
  };

  // --

  const toggleOptions = useMemo(() => {
    // let statusList = [...toggleOelectOptionsLoanStatus];
    let statusListLabels = [
      "All",
      // LOAN_STATUS_LABEL_ALL_ACTIVE,
      ...selectOptionsLoanStatus,
    ];
    let statusLabelsWithCount = statusListLabels.map((status) => ({
      label: getPipelineLabel(filteredPipelineLoans, status),
      value: status,
      count: getStatusWithCount(filteredPipelineLoans, status),
    }));

    // Filter out empty options if navigation prop is passed
    if (navigation) {
      statusLabelsWithCount = statusLabelsWithCount.filter(
        (option) => option.count > 0
      );
    }

    return statusLabelsWithCount;
    // return mappedOptions.sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredPipelineLoans, navigation]);

  if (navigation) {
    if (isEmpty(toggleOptions)) {
      return null;
    }
    return (
      <ToggleNavigation
        className="pipeline-selector"
        tabs={toggleOptions}
        sx={{
          background: grey[100],
          border: `1px solid ${grey[300]}`,
          ".MuiButtonBase-root": {
            fontSize: "10px",
            "&.Mui-selected": {
              fontWeight: "500 !important",
              borderBottomWidth: "1.5px",
              borderTop: "1.5px solid transparent",
            },
          },
        }}
        variant={"underline"}
        value={selectedSort}
        onChange={onSortChange}
      />
    );
  }
  return (
    <TableSortDropdown
      options={toggleOptions}
      value={selectedSort}
      onChange={onSortChange}
      buttonText="Loan Status"
      sx={sx}
    />
  );
};

const LoanPipelineSortDropdown = ({ selectedSort, onSortChange, sx = {} }) => {
  const options = useMemo(
    () => [
      {
        label: "Last Updated (Most Recent)",
        value: {
          colId: "updateDateTime",
          sort: "desc",
        },
      },
      {
        label: "Last Updated (Least Recent)",
        value: {
          colId: "updateDateTime",
          sort: "asc",
        },
      },
      {
        label: "Comment Added (Most Recent)",
        value: {
          colId: "comments",
          sort: "asc",
        },
      },
      {
        label: "Comment Added (Least Recent)",
        value: {
          colId: "comments",
          sort: "desc",
        },
      },
      {
        label: "Created Date (Newest)",
        value: {
          colId: "createDateTime",
          sort: "desc",
        },
      },
      {
        label: "Created Date (Oldest)",
        value: {
          colId: "createDateTime",
          sort: "asc",
        },
      },
    ],
    []
  );

  return (
    <TableSortDropdown
      options={options}
      value={selectedSort}
      onChange={onSortChange}
      buttonText="Sort By"
      sx={sx}
    />
  );
};
export default LoanPipelineScreen;
